/**
 * Cache updaters for a table's views.
 *
 * `qk.views(tableId)` holds the whole list, and every one of these is a change
 * to one entry in it. Pure, and separate from the handlers that call them, so
 * the rule that matters here can be tested against plain arrays.
 *
 * Ported from `web-frontend/modules/database/store/view.js`.
 */
import type { View } from '@/api/types'

/**
 * Adds a view, or replaces the one already held with that id.
 *
 * Upsert rather than insert, for the reason a table's is: `view_created` also
 * fires for a view restored from the trash, which this client may still be
 * holding a copy of.
 */
export function upsertView(views: readonly View[], view: View): View[] {
  const index = views.findIndex((held) => held.id === view.id)
  if (index === -1) return [...views, view]

  const next = [...views]
  next[index] = view
  return next
}

/**
 * Folds a changed view over the one held, **keeping what it does not mention**.
 *
 * This is the whole reason this file exists. `view_updated` deliberately
 * serialises the view without its filters, sortings, group-bys or decorations
 * -- the backend says so in a comment, because each of those has its own
 * endpoint and its own event -- so replacing the held view with the payload
 * would rename a view and silently unfilter it. Worse than silently: the row
 * query's signature would change with it, and the window would refetch and show
 * every row in the table.
 */
export function mergeView(views: readonly View[], view: View): View[] {
  const index = views.findIndex((held) => held.id === view.id)
  if (index === -1) return views as View[]

  const next = [...views]
  next[index] = { ...views[index], ...view }
  return next
}

export function removeView(views: readonly View[], viewId: number): View[] {
  const next = views.filter((view) => view.id !== viewId)
  return next.length === views.length ? (views as View[]) : next
}

/**
 * Renumbers the views to match the order the ids arrive in.
 *
 * The list is sorted by the reader, so the event's job is to write the numbers
 * rather than to rearrange anything. A view the event does not mention keeps
 * the order it had, which is what stops an event about a subset from sending
 * everything else to the front.
 */
export function orderViews(views: readonly View[], order: readonly number[]): View[] {
  return views.map((view) => {
    const index = order.indexOf(view.id)
    return index === -1 ? view : { ...view, order: index + 1 }
  })
}
